import { ref, onScopeDispose } from 'vue';
import { debounce } from 'lodash';

export function useDebounce<Args extends unknown[]>(
  callback: (...args: Args) => unknown,
  delay = 300,
  onError?: (error: Error) => void
) {
  const isPending = ref(false);
  let latestCall: object;

  const debouncedFunc = debounce(async (...args: Args) => {
    const thisCall = (latestCall = {});
    try {
      await callback(...args);
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
    } finally {
      if (thisCall === latestCall) {
        isPending.value = false;
      }
    }
  }, delay);

  const trigger = (...args: Args) => {
    isPending.value = true;
    debouncedFunc(...args);
  };

  const cancel = () => {
    debouncedFunc.cancel();
    isPending.value = false;
  };

  onScopeDispose(() => {
    cancel();
  });

  return {
    trigger,
    cancel,
    flush: debouncedFunc.flush,
    isPending
  };
}
